/**
 * Stats Page - The Intelligent Standard
 *
 * Comprehensive model performance metrics and visualizations.
 * Meridian-only access.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Footer } from '@/components/Footer';
import { Card } from '@/components/layout/Card';
import { HeroPattern } from '@/components/shared/HeroPattern';
import { LoadingState } from '@/components/ui/EmptyState';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import UncertaintyChart, {
  type XAxisMode,
} from '@/components/UncertaintyChart';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

const SOURCES = [
  { label: 'All Sources', value: 'unified' },
  { label: 'Arena & Evals', value: 'arena' },
  { label: 'Seed Rankings', value: 'seed' },
  { label: 'My Seed Rankings', value: 'my-seed' },
  { label: 'LLM Seed Rankings', value: 'llm-seed' },
];

const RATING_OPTIONS = [
  { label: 'Bradley-Terry', value: 'btd' as const },
  { label: 'Elo', value: 'elo' as const },
];

// Back link in hero section
function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 mb-6 text-sm font-medium text-white/70 transition-opacity hover:opacity-70"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M10 4L6 8L10 12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Back to Leaderboard
    </button>
  );
}

// Source filter tabs
function SourceTabs({
  sources,
  value,
  onChange,
}: {
  sources: typeof SOURCES;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {sources.map(src => (
        <button
          key={src.value}
          onClick={() => onChange(src.value)}
          className={`
            px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border
            ${
              value === src.value
                ? 'bg-meridian text-white border-meridian shadow-button'
                : 'bg-white text-slate border-cream-dark hover:border-meridian hover:text-ink'
            }
          `}
        >
          {src.label}
        </button>
      ))}
    </div>
  );
}

// Rankings table row
function RankingRow({
  entry,
  rank,
  ratingType,
  isLast,
}: {
  entry: any;
  rank: number;
  ratingType: 'btd' | 'elo';
  isLast: boolean;
}) {
  return (
    <TableRow className={isLast ? '' : 'border-b border-cream-dark'}>
      <TableCell className="text-sm font-medium text-slate">{rank}</TableCell>
      <TableCell className="font-medium text-ink">
        {entry.display_name}
      </TableCell>
      <TableCell align="center" className="text-lg font-semibold text-meridian">
        {entry.rating != null ? entry.rating : 'N/A'}
      </TableCell>
      <TableCell align="center" className="text-sm text-slate">
        {entry.matches}
      </TableCell>
      <TableCell align="center" className="text-sm font-medium text-success">
        {entry.wins || 0}
      </TableCell>
      <TableCell align="center" className="text-sm font-medium text-error">
        {entry.losses || 0}
      </TableCell>
      <TableCell align="center" className="text-sm text-slate">
        {entry.ties || 0}
      </TableCell>
      <TableCell align="center" className="font-medium text-ink">
        {entry.winRate}%
      </TableCell>
      <TableCell align="center" className="text-xs text-slate">
        {ratingType === 'btd'
          ? `${entry.ciLow} - ${entry.ciHigh}`
          : `${entry.ciLow}% - ${entry.ciHigh}%`}
      </TableCell>
    </TableRow>
  );
}

export default function StatsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const source = searchParams.get('source') || 'seed';
  const ratingType = (searchParams.get('ratingType') as 'elo' | 'btd') || 'btd';

  const [leaderboardData, setLeaderboardData] = useState<any>(null);
  const [evolution, setEvolution] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [xAxisMode, setXAxisMode] = useState<XAxisMode>('votes');

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        let leaderboardUrl = '/api/leaderboard?';
        let evolutionUrl = '/api/leaderboard/evolution?ratingType=btd';

        // Add window parameter for evolution
        const windowParam = xAxisMode === 'date' ? 'daily' : 'votes';
        evolutionUrl += `&window=${windowParam}`;

        if (source === 'llm-seed') {
          leaderboardUrl = `/api/llm-judge/leaderboard?judge_model=gpt-4o&ratingType=${ratingType}`;
          evolutionUrl = `/api/llm-judge/leaderboard/evolution?judge_model=gpt-4o&ratingType=btd&window=${windowParam}`;
        } else if (source === 'my-seed') {
          leaderboardUrl += `source=seed&myRankings=true&ratingType=${ratingType}`;
          evolutionUrl += '&source=seed&myRankings=true';
        } else {
          leaderboardUrl += `source=${source}&ratingType=${ratingType}`;
          evolutionUrl += `&source=${source}`;
        }

        const [leaderboard, evo] = await Promise.all([
          apiRequest(leaderboardUrl),
          ratingType === 'btd'
            ? apiRequest(evolutionUrl).catch(() => null)
            : Promise.resolve(null),
        ]);

        setLeaderboardData(leaderboard);
        setEvolution(evo?.evolution || null);
      } catch (e) {
        logError('Error loading stats', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [source, ratingType, xAxisMode]);

  const updateParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(key, value);
    setSearchParams(newParams);
  };

  const displayNamesMap = new Map<string, string>();
  if (leaderboardData?.entries) {
    leaderboardData.entries.forEach((entry: any) => {
      displayNamesMap.set(entry.model_key, entry.display_name);
    });
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Header */}
      <HeroPattern>
        <div className="max-w-[900px] mx-auto px-8 pt-20 pb-20">
          <BackLink onClick={() => navigate('/leaderboard')} />
          <h1 className="font-serif text-5xl font-medium mb-4 text-white">
            Detailed Statistics
          </h1>
          <p className="text-lg max-w-lg leading-relaxed text-white/70">
            Comprehensive model performance metrics and visualizations
          </p>
        </div>
      </HeroPattern>

      {/* Main Content */}
      <section className="py-6 px-8 pb-16">
        <div className="max-w-[900px] mx-auto">
          {/* Filters */}
          <div className="mb-8 space-y-4">
            <SourceTabs
              sources={SOURCES}
              value={source}
              onChange={v => updateParam('source', v)}
            />

            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider font-medium text-slate">
                Rating
              </span>
              <SegmentedControl
                options={RATING_OPTIONS}
                value={ratingType}
                onChange={v => updateParam('ratingType', v)}
                size="sm"
              />
            </div>
          </div>

          {loading ? (
            <LoadingState message="Loading statistics..." />
          ) : (
            <div className="space-y-8">
              {/* Uncertainty Chart (BTD only) */}
              {ratingType === 'btd' && evolution && (
                <UncertaintyChart
                  evolution={evolution}
                  displayNames={displayNamesMap}
                  defaultTopN={3}
                  defaultMultiplier={1.96}
                  xAxisMode={xAxisMode}
                  onXAxisModeChange={setXAxisMode}
                />
              )}

              {/* Complete Rankings Table */}
              <Card padding="lg">
                <h3 className="font-serif text-xl font-medium mb-6 text-ink">
                  Complete Rankings
                </h3>
                <Table>
                  <TableHead>
                    <TableRow hover={false}>
                      <TableHeaderCell>Rank</TableHeaderCell>
                      <TableHeaderCell>Model</TableHeaderCell>
                      <TableHeaderCell align="center">Rating</TableHeaderCell>
                      <TableHeaderCell align="center">Matches</TableHeaderCell>
                      <TableHeaderCell align="center">Wins</TableHeaderCell>
                      <TableHeaderCell align="center">Losses</TableHeaderCell>
                      <TableHeaderCell align="center">Ties</TableHeaderCell>
                      <TableHeaderCell align="center">Win Rate</TableHeaderCell>
                      <TableHeaderCell align="center">95% CI</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaderboardData?.entries?.map(
                      (entry: any, idx: number) => (
                        <RankingRow
                          key={entry.model_key}
                          entry={entry}
                          rank={idx + 1}
                          ratingType={ratingType}
                          isLast={idx === leaderboardData.entries.length - 1}
                        />
                      )
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
